import { useMutation, useReactiveVar } from '@apollo/client'
import { useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useIntl } from 'react-intl'
import { useLocation, useParams } from 'react-router-dom'

import {
  REMOVE_CUSTOM_COVER,
  UPLOAD_CUSTOM_COVER
} from 'data/layoutcreator/mutations/cover'
import { CHECK_FOR_CUSTOM_COVER } from 'data/layoutcreator/queries/cover'
import { customCoverRefreshCountVar } from 'data/store/cover'
import FileUpload from 'shared/ui/FileUpload'
import UploadZone from 'shared/ui/UploadZone'

const UploadCustomCover = () => {
  const { formatMessage } = useIntl()

  const { projectId } = useParams()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [refreshCount, setRefreshCount] = useState(0)
  const customCoverRefreshCount = useReactiveVar(
    customCoverRefreshCountVar
  )

  const { pathname } = useLocation()
  const side = useMemo(
    () =>
      pathname.split('/').pop() === 'inside' ? 'inside' : 'outside',
    [pathname]
  )

  const [uploadCustomCover] = useMutation(UPLOAD_CUSTOM_COVER, {
    refetchQueries: [
      {
        query: CHECK_FOR_CUSTOM_COVER,
        variables: {
          projectId: projectId!
        }
      }
    ],
    onCompleted: () => {
      customCoverRefreshCountVar(customCoverRefreshCount + 1)
    }
  })

  const [removeCustomCover] = useMutation(REMOVE_CUSTOM_COVER, {
    refetchQueries: [
      {
        query: CHECK_FOR_CUSTOM_COVER,
        variables: {
          projectId: projectId!
        }
      }
    ]
  })

  const handleFileSelect = async (file: File) => {
    const toastId = toast.loading(
      formatMessage({
        id: 'Panel.coverStartEditing.uploadOwnCover.upload.loading'
      })
    )

    const { data } = await uploadCustomCover({
      variables: {
        projectId: projectId!,
        side,
        file
      }
    })

    if (data?.uploadCustomCover) {
      toast.success(
        formatMessage({
          id: 'Panel.coverStartEditing.uploadOwnCover.upload.success'
        }),
        { id: toastId }
      )
      setRefreshCount(refreshCount + 1)
    } else {
      toast.error(
        formatMessage({
          id: 'Panel.coverStartEditing.uploadOwnCover.upload.error'
        }),
        { id: toastId }
      )
    }
  }

  const handleRemoveCustomCover = async () => {
    const toastId = toast.loading(
      formatMessage({
        id: 'Panel.coverStartEditing.uploadOwnCover.remove.loading'
      })
    )

    const { data } = await removeCustomCover({
      variables: {
        projectId: projectId!,
        side
      }
    })

    if (data?.removeCustomCover) {
      toast.success(
        formatMessage({
          id: 'Panel.coverStartEditing.uploadOwnCover.remove.success'
        }),
        { id: toastId }
      )
    } else {
      toast.error(
        formatMessage({
          id: 'Panel.coverStartEditing.uploadOwnCover.remove.error'
        }),
        { id: toastId }
      )
    }
  }

  return (
    <>
      <UploadZone
        labelId="Panel.startEditing.uploadOwnCover.label"
        onClick={() => {
          fileInputRef.current?.click()
        }}
        isDragActive={false}
      />

      <FileUpload ref={fileInputRef} onSelect={handleFileSelect} />
    </>
  )
}

export default UploadCustomCover
